<template>
  <div class="lang-switcher" ref="switcherRef">
    <button class="lang-btn" @click="toggleOpen" :aria-label="`Language: ${currentLabel}`">
      <!-- Globe icon (inline SVG — not in AppIcon registry) -->
      <svg
        class="globe-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
      <span class="lang-label">{{ currentLabel }}</span>
    </button>

    <Transition name="dropdown">
      <div v-if="open" class="lang-dropdown" role="listbox" :aria-label="'Select language'">
        <button
          v-for="loc in SUPPORTED_LOCALES"
          :key="loc"
          class="lang-option"
          :class="{ active: loc === currentLocale }"
          role="option"
          :aria-selected="loc === currentLocale"
          @click="selectLocale(loc)"
        >
          {{ LOCALE_NAMES[loc] }}
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { SUPPORTED_LOCALES, setLocale } from '@/i18n'
import type { SupportedLocale } from '@/i18n'
import { useUserStore } from '@/stores/user'
import apiClient from '@/api/client'

const LOCALE_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  de: 'Deutsch',
  pt: 'Português',
  es: 'Español',
  fr: 'Français',
  tr: 'Türkçe',
}

const { locale } = useI18n()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const open = ref(false)
const switcherRef = ref<HTMLElement | null>(null)

const currentLocale = computed(() => locale.value as SupportedLocale)
const currentLabel = computed(() => LOCALE_NAMES[currentLocale.value] ?? currentLocale.value)

function toggleOpen() {
  open.value = !open.value
}

function closeDropdown() {
  open.value = false
}

async function selectLocale(loc: SupportedLocale) {
  closeDropdown()

  if (loc === currentLocale.value) return

  await setLocale(loc)

  // Persist to player profile if logged in
  if (userStore.isAuthenticated) {
    try {
      await apiClient.patch('/api/user/locale', { locale: loc })
    } catch {
      // Non-critical — locale is already saved to localStorage
    }
  }

  // Update URL prefix for locale-aware routes (e.g. CMS pages: /:locale/pages/:slug)
  if (route.params.locale && SUPPORTED_LOCALES.includes(route.params.locale as SupportedLocale)) {
    router.push({ name: route.name ?? undefined, params: { ...route.params, locale: loc } })
  }
}

function handleClickOutside(e: MouseEvent) {
  if (switcherRef.value && !switcherRef.value.contains(e.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.lang-switcher {
  position: relative;
  flex-shrink: 0;
}

.lang-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
  white-space: nowrap;
}

.lang-btn:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

.globe-icon {
  flex-shrink: 0;
  color: currentColor;
}

.lang-label {
  line-height: 1;
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 140px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
  padding: 4px;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  z-index: 300;
}

.lang-option {
  display: block;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: var(--radius-sm, 4px);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  text-align: left;
  transition: background 150ms ease, color 150ms ease;
}

.lang-option:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.lang-option.active {
  color: var(--accent-primary, #00e701);
  font-weight: 600;
}

/* Dropdown transition */
.dropdown-enter-active {
  animation: fadeIn 150ms ease forwards;
}
.dropdown-leave-active {
  animation: fadeOut 100ms ease forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
}

/* Compact on mobile: hide label, show globe only */
@media (max-width: 768px) {
  .lang-label {
    display: none;
  }

  .lang-btn {
    padding: 0 8px;
    width: 34px;
    justify-content: center;
  }
}
</style>
